import { useEffect, useMemo } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import HomeHeader from '@/components/HomeHeader';
import Footer from '@/components/homepage/Footer';
import ResourceFAQSection from '@/components/resources/ResourceFAQSection';
import TableOfContents from '@/components/blog/TableOfContents';
import BlogFeedback from '@/components/blog/BlogFeedback';
import EmailCapture from '@/components/EmailCapture';
import RelatedPosts from '@/components/blog/RelatedPosts';
import { usePageMeta } from '@/hooks/usePageMeta';
import { blogJsonLdGraph, getBlogPost, withHeadingAnchors, MID_ARTICLE_CTA_MARKER } from '@/lib/data/blog';
import { capture } from '@/lib/analytics/posthog';

const JSONLD_ID = 'article-jsonld';

export default function BlogPost() {
    const { slug } = useParams<{ slug: string }>();
    const post = slug ? getBlogPost(slug) : undefined;

    usePageMeta(post?.title, post?.description, post ? { publishedTime: post.date } : undefined);

    const { html: bodyHtml, headings } = useMemo(
        () => withHeadingAnchors(post?.body ?? ''),
        [post],
    );

    // A post can embed MID_ARTICLE_CTA_MARKER once to place a CTA partway through
    // instead of only at the footer — the footer CTA on our one post with proven
    // organic search traffic converts 0% over 30 days (MSF-269).
    const [bodyBeforeMidCta, bodyAfterMidCta] = useMemo(
        () => (bodyHtml.includes(MID_ARTICLE_CTA_MARKER)
            ? bodyHtml.split(MID_ARTICLE_CTA_MARKER)
            : [bodyHtml, null]),
        [bodyHtml],
    );

    // Inject Article + FAQPage JSON-LD graph into <head>; mirrors the pattern on /resources/*.
    useEffect(() => {
        if (!post) return;
        const existing = document.getElementById(JSONLD_ID);
        if (existing) existing.remove();

        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.id = JSONLD_ID;
        script.text = JSON.stringify(blogJsonLdGraph(post));
        document.head.appendChild(script);

        return () => {
            document.getElementById(JSONLD_ID)?.remove();
        };
    }, [post]);

    // Unknown slug → send to the blog index rather than a blank page.
    if (!post) {
        return <Navigate to="/blog" replace />;
    }

    return (
        <div className="min-h-screen bg-white flex flex-col">
            <HomeHeader />
            <article className="max-w-3xl mx-auto px-4 py-12 w-full">
                <nav className="mb-6 text-sm">
                    <Link to="/blog" className="text-blue-600 hover:text-blue-700 font-medium">
                        ← Education Insights
                    </Link>
                </nav>

                <header className="mb-8">
                    <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
                        <time dateTime={post.date}>{post.displayDate}</time>
                        <span aria-hidden="true">·</span>
                        <span>{post.readingTime}</span>
                    </div>
                    <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-gray-900">
                        {post.title}
                    </h1>
                </header>

                <TableOfContents headings={headings} />

                <div
                    className="prose prose-lg max-w-none prose-headings:font-bold prose-a:text-blue-600 hover:prose-a:text-blue-700"
                    // Content is first-party, author-written HTML from src/lib/data/blog.ts.
                    dangerouslySetInnerHTML={{ __html: bodyBeforeMidCta }}
                />

                {bodyAfterMidCta !== null && (
                    <>
                        <div className="my-8 rounded-2xl border border-primary/20 bg-primary/5 p-6 not-prose">
                            <p className="text-base font-semibold text-gray-900 mb-1">
                                Start your own Fund Page
                            </p>
                            <p className="text-sm text-gray-600 mb-4">
                                Set it up in a few minutes, then invite the people in your corner to give
                                once or monthly. Always free for students — payments go straight to your
                                bank account via Stripe.
                            </p>
                            <Link
                                to="/signup-creator"
                                className="inline-flex items-center rounded-full bg-blue-600 px-5 py-2.5 text-sm text-white font-medium hover:bg-blue-700 transition-colors"
                                onClick={() => capture('cta_clicked', { cta: 'start_fund_page', location: 'blog_post_mid_article', slug: post.slug })}
                            >
                                Start your Fund Page — free
                            </Link>
                        </div>
                        <div
                            className="prose prose-lg max-w-none prose-headings:font-bold prose-a:text-blue-600 hover:prose-a:text-blue-700"
                            dangerouslySetInnerHTML={{ __html: bodyAfterMidCta }}
                        />
                    </>
                )}

                <ResourceFAQSection faqs={post.faqs} />

                {post.slug.includes('parent-plus') && (
                    <div className="mt-10 rounded-2xl border border-primary/20 bg-primary/5 p-6">
                        <p className="text-base font-semibold text-gray-900 mb-1">
                            See your exact Parent PLUS gap
                        </p>
                        <p className="text-sm text-gray-600 mb-4">
                            Free 60-second calculator — enter your cost of attendance and aid to see the
                            gap the new cap leaves, and how many monthly backers would close it.
                        </p>
                        <Link
                            to="/parent-plus"
                            className="inline-flex items-center rounded-full bg-primary px-5 py-2.5 text-sm text-white font-medium hover:bg-primary/90 transition-colors"
                            onClick={() => capture('cta_clicked', { cta: 'parent_plus_calculator', location: 'blog_post', slug: post.slug })}
                        >
                            Calculate my gap
                        </Link>
                    </div>
                )}

                <EmailCapture
                    context={`blog-${post.slug}`}
                    heading="Not ready to start a Fund Page yet?"
                    subheading="Leave your email and we'll send more scholarship & funding tips like this — no account needed."
                    cta="Email me tips"
                />

                <BlogFeedback slug={post.slug} />

                <RelatedPosts currentSlug={post.slug} />

                <hr className="my-10 border-gray-200" />
                <p className="text-sm text-gray-600 italic">
                    My Study Fund is a platform where students set up a Fund Page and invite the
                    people in their corner to become monthly supporters of their education. Always
                    free for students. Payments by Stripe, paid directly to the student’s bank
                    account.
                </p>
                <div className="mt-8">
                    <Link
                        to="/signup-creator"
                        className="inline-flex items-center rounded-full bg-blue-600 px-6 py-3 text-white font-medium hover:bg-blue-700 transition-colors"
                        onClick={() => capture('cta_clicked', { cta: 'start_fund_page', location: 'blog_post_footer', slug: post.slug })}
                    >
                        Start your Fund Page — free
                    </Link>
                </div>
            </article>
            <Footer />
        </div>
    );
}
